import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronRight, ArrowRight, Phone, Zap, Flame, Sun, Wind, RotateCcw, CheckCircle2, Info,
} from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

/**
 * Rebate Finder — Victorian hot water upgrade rebates.
 * Based on publicly-available programs (Victorian Energy Upgrades, Solar Victoria,
 * federal STC scheme). Estimates — real values depend on install, household income,
 * meter type and current certificate prices. Always finalised at quote time.
 */

const SYSTEMS = [
  { key: "electric", label: "Electric Storage", icon: Zap, desc: "Old-school tank with element inside" },
  { key: "gas", label: "Gas Storage / Instant", icon: Flame, desc: "Gas-fired tank or wall unit" },
  { key: "solar", label: "Solar Hot Water", icon: Sun, desc: "Roof panels with tank" },
  { key: "heatpump", label: "Heat Pump", icon: Wind, desc: "Air-source heat pump system" },
];

const NEW_SYSTEMS = [
  { key: "heatpump", label: "Heat Pump", icon: Wind, desc: "Up to 70% less energy than electric" },
  { key: "solar", label: "Solar Hot Water", icon: Sun, desc: "Roof panels + tank, 60–80% sun powered" },
];

// Rough rebate pool (indicative only — real values vary)
const RULES = {
  // current -> new -> bands
  electric: {
    heatpump: {
      veu: { low: 800, high: 2800, label: "Victorian Energy Upgrades (VEU)" },
      stc:  { low: 500, high: 1800, label: "Federal STC (upfront discount)" },
      sv:   { low: 0,   high: 1000, label: "Solar Victoria — eligible households" },
    },
    solar: {
      veu: { low: 400, high: 1600, label: "Victorian Energy Upgrades (VEU)" },
      stc: { low: 800, high: 2400, label: "Federal STC (upfront discount)" },
      sv:  { low: 0,   high: 1000, label: "Solar Victoria — eligible households" },
    },
  },
  gas: {
    heatpump: {
      veu: { low: 500, high: 1800, label: "Victorian Energy Upgrades (VEU)" },
      stc: { low: 500, high: 1800, label: "Federal STC (upfront discount)" },
      sv:  { low: 0,   high: 1000, label: "Solar Victoria — eligible households" },
    },
    solar: {
      veu: { low: 300, high: 1200, label: "Victorian Energy Upgrades (VEU)" },
      stc: { low: 800, high: 2400, label: "Federal STC (upfront discount)" },
      sv:  { low: 0,   high: 1000, label: "Solar Victoria — eligible households" },
    },
  },
  solar: {
    heatpump: {
      stc: { low: 500, high: 1500, label: "Federal STC (upfront discount)" },
    },
  },
  heatpump: {
    solar: {
      stc: { low: 500, high: 1500, label: "Federal STC (upfront discount)" },
    },
  },
};

const inVictoria = (postcode) => {
  const n = parseInt(postcode, 10);
  if (isNaN(n)) return false;
  // VIC: 3000–3999 + 8000–8999 (PO boxes)
  return (n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999);
};

const sum = (bands) => Object.values(bands).reduce((acc, b) => ({ low: acc.low + b.low, high: acc.high + b.high }), { low: 0, high: 0 });

const RebateFinder = () => {
  const [step, setStep] = useState(1);
  const [postcode, setPostcode] = useState("");
  const [current, setCurrent] = useState(null);
  const [next, setNext] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(1);
    setPostcode("");
    setCurrent(null);
    setNext(null);
    setResult(null);
  };

  const computeResult = (c, n) => {
    const bands = RULES[c]?.[n];
    if (!bands) {
      return { eligible: false, note: "No standard rebate applies for that specific swap. Call us — we may still find savings." };
    }
    const total = sum(bands);
    return { eligible: true, bands, total };
  };

  const submitPostcode = (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(postcode)) {
      alert("Enter a valid 4-digit Australian postcode.");
      return;
    }
    if (!inVictoria(postcode)) {
      setResult({
        outOfState: true,
        note: "The main rebates on this page are for Victorian households. Federal STCs still apply nationwide — call us for an estimate.",
      });
      setStep(5);
      return;
    }
    setStep(2);
  };

  const chooseCurrent = (sys) => {
    setCurrent(sys);
    setStep(3);
  };

  const chooseNext = (sys) => {
    setNext(sys);
    const r = computeResult(current, sys);
    setResult(r);
    setStep(4);
  };

  return (
    <div data-testid="rebate-finder-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <Link to="/tools" className="text-muted hover:text-gold">Tools</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Rebate Finder</span>
          </nav>
          <div className="max-w-3xl">
            <div className="eyebrow mb-6"><span>Free Tool · Victorian Rebates</span></div>
            <h1 className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink">
              Hot water upgrade.
              <br />
              <span className="text-gold">Who's paying for it?</span>
            </h1>
            <p className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed">
              A new heat pump or solar hot water system is cheaper than you think once rebates
              are stacked. Three questions and we'll give you a ballpark.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <div className="flex gap-2 mb-10 max-w-xl">
            {[1,2,3,4].map((n) => (
              <div key={n} className={`h-[3px] flex-1 transition-colors duration-500 ${n <= step ? "bg-gold" : "bg-line"}`} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
              data-testid={`rebate-step-${step}`}
              className="bg-card border border-line p-8 md:p-12 max-w-3xl"
            >
              {step === 1 && (
                <form onSubmit={submitPostcode}>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 1 of 3</div>
                  <h2 className="font-display text-3xl md:text-4xl text-ink mb-5">Where are you?</h2>
                  <p className="text-muted mb-6 max-w-md">
                    Enter your postcode. Most rebates here are Victoria-only.
                  </p>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={4}
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="3000"
                    data-testid="rebate-postcode"
                    className="w-full md:w-56 bg-transparent border-b border-line focus:border-gold text-ink text-3xl font-body font-medium py-3 outline-none tabular-nums"
                  />
                  <div className="mt-8">
                    <button type="submit" className="btn-gold" data-testid="rebate-postcode-submit">
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 2 of 3</div>
                  <h2 className="font-display text-3xl md:text-4xl text-ink mb-5">
                    What's heating your water today?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
                    {SYSTEMS.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => chooseCurrent(s.key)}
                        data-testid={`rebate-current-${s.key}`}
                        className="group flex items-start gap-4 p-5 border border-line hover:border-gold hover:bg-cardHover text-left transition-colors"
                      >
                        <div className="w-10 h-10 border border-gold/30 bg-gold/5 flex items-center justify-center flex-shrink-0">
                          <s.icon size={16} className="text-gold" />
                        </div>
                        <div>
                          <div className="font-display text-lg text-ink group-hover:text-gold transition-colors">{s.label}</div>
                          <div className="text-muted text-sm mt-1">{s.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 3 of 3</div>
                  <h2 className="font-display text-3xl md:text-4xl text-ink mb-5">
                    What are you upgrading to?
                  </h2>
                  <p className="text-muted mb-6 max-w-md">
                    Both options get rebates. Heat pumps usually win on install cost + running cost.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
                    {NEW_SYSTEMS.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => chooseNext(s.key)}
                        data-testid={`rebate-next-${s.key}`}
                        className="group flex items-start gap-4 p-5 border border-line hover:border-gold hover:bg-cardHover text-left transition-colors"
                      >
                        <div className="w-10 h-10 border border-gold/30 bg-gold/5 flex items-center justify-center flex-shrink-0">
                          <s.icon size={16} className="text-gold" />
                        </div>
                        <div>
                          <div className="font-display text-lg text-ink group-hover:text-gold transition-colors">{s.label}</div>
                          <div className="text-muted text-sm mt-1">{s.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 4 && result && (
                <div data-testid="rebate-result">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 size={26} className="text-gold" />
                    <span className="font-display text-xl md:text-2xl uppercase text-gold">Your estimate</span>
                  </div>
                  {result.eligible ? (
                    <>
                      <div className="font-body font-medium text-5xl md:text-6xl text-ink mb-1 tabular-nums">
                        ${result.total.low.toLocaleString()} – ${result.total.high.toLocaleString()}
                      </div>
                      <div className="text-muted text-xs tracking-widerx uppercase mb-10">
                        Total potential rebate · varies with household & install
                      </div>
                      <div className="space-y-3">
                        {Object.entries(result.bands).map(([k, b]) => (
                          <div key={k} className="flex items-start justify-between py-4 border-t border-line gap-4">
                            <div>
                              <div className="text-ink font-display tracking-wide text-base md:text-lg">{b.label}</div>
                              <div className="text-muted text-xs tracking-widerx uppercase mt-1">Rebate program</div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="font-body font-medium text-xl text-gold tabular-nums">
                                ${b.low.toLocaleString()}–{b.high.toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-8 p-4 border-l-2 border-gold/40 bg-gold/[0.03] flex items-start gap-3">
                        <Info size={16} className="text-gold mt-0.5 flex-shrink-0" />
                        <p className="text-muted text-sm leading-relaxed">
                          Figures are indicative, not guaranteed. Real rebate values depend on your
                          specific system, household income, current certificate prices, and the
                          accredited installer rebate flow. We finalise exact figures in the quote
                          and deduct eligible rebates off your invoice — you don't lodge any paperwork.
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted leading-relaxed">{result.note}</p>
                  )}
                  <div className="mt-10 flex flex-wrap gap-3">
                    <a href={PHONE_HREF} className="btn-gold" data-testid="rebate-cta-call">
                      <Phone size={16} /> Lock in a quote
                    </a>
                    <Link to="/contact" className="btn-ghost">Request a Quote <ArrowRight size={16} /></Link>
                    <button onClick={reset} className="btn-ghost" data-testid="rebate-reset">
                      <RotateCcw size={14} /> Start again
                    </button>
                  </div>
                </div>
              )}

              {step === 5 && result?.outOfState && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Out of Area</div>
                  <h2 className="font-display text-3xl md:text-4xl text-ink mb-5">
                    Looks like you're outside Victoria.
                  </h2>
                  <p className="text-muted mb-6 max-w-md">{result.note}</p>
                  <button onClick={reset} className="btn-ghost" data-testid="rebate-reset-out">
                    <RotateCcw size={14} /> Try another postcode
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

export default RebateFinder;
